import React, { useState, useEffect } from 'react';
import RegisterTeam from '../components/RegisterTeam';
import RegisterTeamCsv from '../components/RegisterTeamCsv';
import RegisterSimple from '../components/RegisterSimple';
import DepartmentManager from '../components/DepartmentManager';
import DepartmentContractMapper from '../components/DepartmentContractMapper';
import ContractManager from '../components/ContractManager';
import RegisterOrgWithProducts from '../components/RegisterOrgWithProducts';
import CreateSuperUser from '../components/CreateSuperUser';

export default function Settings() {
  const [sessionUser, setSessionUser] = useState(null);
  // Stage 8 cutover: default to 'team-csv' since 'team' tab was retired
  const [activeTab, setActiveTab] = useState('team-csv');
  const [selectedApp, setSelectedApp] = useState('');

  useEffect(() => {
    const loadSession = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/session`, {
          credentials: 'include'
        });
        const data = await res.json();

        if (data.success && data.user) {
          setSessionUser(data.user);
          localStorage.setItem('sessionUser', JSON.stringify(data.user));
        } else {
          setSessionUser(null);
          localStorage.removeItem('sessionUser');
        }
      } catch (err) {
        console.error('❌ Failed to load session:', err.message);
        setSessionUser(null);
        localStorage.removeItem('sessionUser');
      }
    };

    loadSession();
  }, []);

  const isSuperAdmin = sessionUser?.role === 'superadmin';
  const isSuperUser = sessionUser?.role === 'superuser';

  if (!isSuperAdmin && !isSuperUser) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="bg-slate-800/50 backdrop-blur-xl rounded-2xl p-8 border border-slate-700/50 shadow-2xl text-center">
          <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-3xl">❌</span>
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">Access Denied</h2>
          <p className="text-slate-400">Only Super Admins or SuperUsers can access Settings.</p>
        </div>
      </div>
    );
  }

  // Stage 8 cutover: list of tabs that have been retired and replaced.
  // If the activeTab somehow lands on one of these (e.g., bookmarked URL),
  // show a friendly notice instead of the old form.
  const RETIRED_TABS = {
    org:         { title: 'Organization Provisioning', destination: '/platform-admin', label: 'Platform Admin Console' },
    team:        { title: 'Full Team Registration', destination: '/people', label: 'People page' },
    simple:      { title: 'Add Associate / Admin', destination: '/people', label: 'People page' },
    departments: { title: 'Department Manager', destination: '/people', label: 'People page (or the first-login Onboarding wizard for new orgs)' },
  };

  const renderContent = () => {
    if (RETIRED_TABS[activeTab]) {
      const r = RETIRED_TABS[activeTab];
      return (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-2xl p-6">
          <h3 className="text-lg font-semibold text-white mb-2">{r.title} has moved</h3>
          <p className="text-sm text-slate-300 mb-4">
            This flow now lives in the <strong className="text-amber-300">{r.label}</strong>.
          </p>
          <a
            href={r.destination}
            className="inline-block px-4 py-2 bg-gradient-to-r from-orange-500 to-amber-500 rounded-lg text-sm font-medium text-white hover:shadow-lg hover:shadow-blue-500/25 transition"
          >
            Go to {r.label} →
          </a>
        </div>
      );
    }

    switch (activeTab) {
      case 'team-csv':
        return <RegisterTeamCsv />;
      case 'contracts':
        return <ContractManager selectedApp={selectedApp} />;
      case 'mapping':
        return <DepartmentContractMapper />;
      default:
        return <RegisterTeamCsv />;
    }
  };

  const tabClass = (key) =>
    `w-full text-left px-4 py-3 rounded-xl transition-all duration-200 font-medium ${
      activeTab === key 
        ? 'bg-gradient-to-r from-orange-500 to-amber-500 text-white shadow-lg shadow-blue-500/25' 
        : 'text-slate-300 hover:text-white hover:bg-slate-700/50'
    }`;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex">
      {/* Sidebar */}
      <aside className="w-64 bg-slate-800/50 backdrop-blur-xl border-r border-slate-700/50 shadow-2xl">
        <div className="p-6 border-b border-slate-700/50">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-amber-500 rounded-xl flex items-center justify-center">
              <span className="text-xl">⚙️</span>
            </div>
            <div>
              <h1 className="text-lg font-bold text-white">Super Admin Panel</h1>
              <p className="text-xs text-slate-400">Control Center</p>
            </div>
          </div>
        </div>

        {/*
          Stage 8 cutover: the navigation is no longer gated on memo product.
          Replaced tabs (team, simple, departments, org) are hidden — those
          flows now live in /people and /platform-admin. Remaining tabs are
          legacy features not yet ported to the new module:
            - team-csv: bulk CSV team import
            - contracts: contract + PBP registry
            - mapping: department-to-contract mapping
        */}
        <nav className="flex flex-col gap-2 p-4">
          {/* Stage 8d: escape hatches to the new module from this legacy page */}
          {isSuperAdmin && (
            <a
              href="/platform-admin"
              className="w-full text-left px-4 py-3 rounded-xl transition-all duration-200 font-medium text-amber-300 hover:text-white hover:bg-amber-500/20 border border-amber-500/30 mb-1"
            >
              Platform Console →
            </a>
          )}
          <a
            href="/people"
            className="w-full text-left px-4 py-3 rounded-xl transition-all duration-200 font-medium text-blue-300 hover:text-white hover:bg-blue-500/20 border border-blue-500/30 mb-3"
          >
            👥 People →
          </a>

          <div className="border-t border-slate-700/50 my-1" />
          <p className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold px-2 mb-1">Legacy</p>

          <button onClick={() => setActiveTab('team-csv')} className={tabClass('team-csv')}>
            📄 Full Team via CSV
          </button>
          <button onClick={() => setActiveTab('contracts')} className={tabClass('contracts')}>
            📄 Contracts &amp; PBPs
          </button>
          <button onClick={() => setActiveTab('mapping')} className={tabClass('mapping')}>
            🔗 Department Mapping
          </button>
        </nav>
      </aside>

      {/* Main Content */}
      <main className="flex-1 px-8 py-8">
        {/* Stage 8 cutover banner — directs to the new flows */}
        <div className="mb-6 bg-gradient-to-r from-orange-500/15 via-amber-500/15 to-orange-500/15 border border-orange-500/40 rounded-2xl p-5">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-xl bg-blue-500/20 border border-blue-500/40 flex items-center justify-center flex-shrink-0">
              <span className="text-xl">ℹ️</span>
            </div>
            <div className="flex-1">
              <h3 className="text-base font-semibold text-white mb-1">User management has moved</h3>
              <p className="text-sm text-slate-300 leading-relaxed">
                Adding people, replacing leavers, and managing departments now lives in the new <strong className="text-orange-300">People</strong> page (people icon in the left rail). Provisioning new customer organizations lives in the <strong className="text-amber-300">Platform Admin Console</strong> at <code className="bg-slate-800/60 px-1.5 py-0.5 rounded text-xs">/platform-admin</code>.
              </p>
              <p className="text-xs text-slate-500 mt-2">
                This page is kept for the three legacy flows below that haven't been ported yet: bulk CSV team import, the contract + PBP registry, and department-to-contract mapping.
              </p>
            </div>
          </div>
        </div>

        <header className="mb-8">
          <h1 className="text-4xl font-bold text-white mb-2">Legacy Settings</h1>
          <p className="text-slate-400 text-lg">
            Contracts, PBPs, department mapping, and bulk CSV imports.
          </p>
        </header>

        <div className="mb-8">
          <label className="block text-sm font-medium text-slate-300 mb-3">Select App</label>
          <select
            value={selectedApp}
            onChange={(e) => setSelectedApp(e.target.value)}
            required
            className="w-full max-w-xs bg-slate-700/50 border border-slate-600/50 rounded-xl px-4 py-3 text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all duration-200"
          >
            <option value="">Select App</option>
            {sessionUser?.organization_products?.memo && (
              <option value="memo">Memo</option>
            )}
            {sessionUser?.organization_products?.cap && (
              <option value="cap">CAP</option>
            )}
            {sessionUser?.organization_products?.contract && (
              <option value="contract">Contract</option>
            )}
            {sessionUser?.organization_products?.scrubber && (
              <option value="scrubber">Scrubber</option>
            )}
          </select>
        </div>

        <div className="space-y-8">{renderContent()}</div>
      </main>
    </div>
  );
}
